import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
    ArrowUpRight,
    ArrowLeft,
    RotateCcw,
    CheckCircle2,
    CircleDot,
    Circle,
    UtensilsCrossed,
    Zap,
    Users,
    HandHeart,
    BarChart3,
    Sprout,
    Leaf,
    Globe2,
    Timer,
    ClipboardCheck,
    WalletMinimal,
    ArrowRight,
} from "lucide-react";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/primitives";
import { API } from "../lib/site";

const EASE = [0.22, 1, 0.36, 1];

const QUESTIONS = [
    {
        area: "Food & Waste",
        icon: UtensilsCrossed,
        text: "Does your hotel actively measure and reduce food waste and other operational waste?",
    },
    {
        area: "Energy & Water",
        icon: Zap,
        text: "Does your hotel regularly monitor and take action to reduce unnecessary energy and water consumption?",
    },
    {
        area: "People",
        icon: Users,
        text: "Are employees trained and actively involved in the hotel's sustainability practices?",
    },
    {
        area: "Community & Local Impact",
        icon: HandHeart,
        text: "Does your hotel support local communities, local suppliers, culture, or environmental initiatives?",
    },
    {
        area: "Management & Measurement",
        icon: BarChart3,
        text: "Does your hotel have sustainability goals and regularly measure progress toward them?",
    },
];

const ANSWERS = [
    { key: "yes", label: "Yes", sub: "Fully implemented", points: 2, icon: CheckCircle2 },
    { key: "partly", label: "Partly", sub: "Some practices are in place", points: 1, icon: CircleDot },
    { key: "notyet", label: "Not yet", sub: "This is an opportunity for improvement", points: 0, icon: Circle },
];

const BANDS = [
    {
        min: 8,
        icon: Sprout,
        title: "Strong Foundation",
        text: "Your hotel already has several important sustainability practices in place. The next step is strengthening measurement, identifying improvement opportunities, and building toward greater positive and regenerative impact.",
    },
    {
        min: 4,
        icon: Leaf,
        title: "Growing Potential",
        text: "Your hotel has started its sustainability journey and has meaningful opportunities to strengthen its practices, measurement, and impact.",
    },
    {
        min: 0,
        icon: Globe2,
        title: "Ready to Begin",
        text: "Every sustainability journey starts somewhere. GGH can help you identify practical first steps toward reducing waste, improving resource efficiency, and building more responsible hotel operations.",
    },
];

const JOURNEY = [
    "Take the Free Check",
    "Discover Your Starting Point",
    "See Opportunities",
    "Join GGH",
    "Complete the Full Assessment",
    "Improve & Track Progress",
];

const answerLabel = (points) =>
    points === 2 ? "Yes — Fully implemented" : points === 1 ? "Partly — In progress" : "Not yet — Opportunity";

const MiniAssessment = () => {
    const [stage, setStage] = useState("intro"); // intro | quiz | result
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState(Array(QUESTIONS.length).fill(null));
    const [lead, setLead] = useState({ hotel_name: "", email: "" });
    const [leadErrors, setLeadErrors] = useState({});
    const [leadStatus, setLeadStatus] = useState("idle");
    const cardRef = useRef(null);

    const score = answers.reduce((sum, a) => sum + (a ?? 0), 0);
    const band = BANDS.find((b) => score >= b.min);

    const scrollToCard = () =>
        cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

    const start = () => {
        setStage("quiz");
        setStep(0);
        scrollToCard();
    };

    const choose = (qi, points) => {
        if (answers[qi] !== null) return;
        const next = [...answers];
        next[qi] = points;
        setAnswers(next);
        setTimeout(() => {
            if (qi < QUESTIONS.length - 1) {
                setStep(qi + 1);
            } else {
                setStage("result");
                scrollToCard();
            }
        }, 340);
    };

    const retake = () => {
        setAnswers(Array(QUESTIONS.length).fill(null));
        setStep(0);
        setStage("quiz");
        setLead({ hotel_name: "", email: "" });
        setLeadErrors({});
        setLeadStatus("idle");
        scrollToCard();
    };

    const submitLead = async (e) => {
        e.preventDefault();
        const er = {};
        if (lead.hotel_name.trim().length < 2) er.hotel_name = "Please enter your hotel or business name.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email)) er.email = "Please enter a valid business email.";
        if (Object.keys(er).length) {
            setLeadErrors(er);
            return;
        }
        setLeadStatus("submitting");
        try {
            await axios.post(`${API}/mini-check/lead`, {
                hotel_name: lead.hotel_name.trim(),
                email: lead.email.trim(),
                score,
                band: band.title,
            });
            setLeadStatus("success");
        } catch {
            setLeadStatus("idle");
            toast.error("Something went wrong. Please try again or email us directly.");
        }
    };

    const q = QUESTIONS[step];

    return (
        <main data-testid="mini-check-page">
            <SEO
                title="Free Sustainability Check | Global Green Hotel"
                description="Take the free 2-minute GGH Sustainability Check — five quick questions to see where your hotel is making progress and where opportunities for improvement may exist."
            />
            <PageHero
                testid="mini-check-hero"
                eyebrow="Free GGH Sustainability Check"
                titleLines={["How Sustainable Is", "Your Hotel?"]}
                description="Take this free 2-minute GGH Sustainability Check to see where your hotel is making progress and where opportunities for improvement may exist. No payment is required."
            />

            <section className="bg-[#F2EFE7] py-20 sm:py-28">
                <div ref={cardRef} className="mx-auto max-w-[820px] scroll-mt-28 px-5 sm:px-8">
                    <AnimatePresence mode="wait">
                        {stage === "intro" && (
                            <motion.div
                                key="intro"
                                data-testid="mini-check-intro"
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -16 }}
                                transition={{ duration: 0.5, ease: EASE }}
                                className="rounded-2xl border border-[#E8E6E1] bg-white p-8 shadow-[0_24px_70px_rgba(10,23,17,0.08)] sm:p-12"
                            >
                                <Eyebrow>Before You Begin</Eyebrow>
                                <h2 className="font-display mt-4 text-2xl font-medium tracking-tight text-[#0A1711] sm:text-3xl">
                                    Five questions. Two minutes. A clearer starting point.
                                </h2>
                                <div className="mt-8 grid gap-3 sm:grid-cols-3">
                                    {[
                                        { icon: ClipboardCheck, label: "5 simple questions" },
                                        { icon: Timer, label: "About 2 minutes" },
                                        { icon: WalletMinimal, label: "No payment or sign-up" },
                                    ].map(({ icon: Icon, label }) => (
                                        <div
                                            key={label}
                                            className="flex items-center gap-3 rounded-xl border border-[#E8E6E1] bg-[#FDFBF7] px-4 py-3.5"
                                        >
                                            <Icon size={18} className="shrink-0 text-[#A4803F]" aria-hidden="true" />
                                            <span className="text-sm font-semibold text-[#0A1711]">{label}</span>
                                        </div>
                                    ))}
                                </div>
                                <ol
                                    className="mt-8 flex flex-wrap items-center gap-2"
                                    data-testid="mini-check-journey"
                                    aria-label="Your GGH journey"
                                >
                                    {JOURNEY.map((s, i) => (
                                        <li key={s} className="flex items-center gap-2">
                                            <span
                                                className={`rounded-full border px-3.5 py-1.5 text-xs font-bold ${
                                                    i === 0
                                                        ? "border-[#C2A160] bg-[#C2A160]/10 text-[#A4803F]"
                                                        : "border-[#1A3B2B]/20 text-[#1A3B2B]/80"
                                                }`}
                                            >
                                                {s}
                                            </span>
                                            {i < JOURNEY.length - 1 && (
                                                <ArrowRight size={13} className="text-[#C2A160]" aria-hidden="true" />
                                            )}
                                        </li>
                                    ))}
                                </ol>
                                <p className="mt-8 text-sm leading-relaxed text-[#4A5D54]">
                                    Answer honestly — this check is for you. Your responses are not
                                    stored, shared or published, and you will see your result
                                    immediately.
                                </p>
                                <button
                                    type="button"
                                    data-testid="mini-check-start"
                                    onClick={start}
                                    className="btn-gold mt-8"
                                >
                                    Start the Free Check
                                    <ArrowUpRight size={16} aria-hidden="true" />
                                </button>
                            </motion.div>
                        )}

                        {stage === "quiz" && (
                            <motion.div
                                key={`q-${step}`}
                                data-testid="mini-check-question"
                                initial={{ opacity: 0, x: 32 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -24 }}
                                transition={{ duration: 0.4, ease: EASE }}
                                className="rounded-2xl border border-[#E8E6E1] bg-white p-8 shadow-[0_24px_70px_rgba(10,23,17,0.08)] sm:p-12"
                            >
                                <div className="flex items-center justify-between gap-4">
                                    <p
                                        data-testid="mini-check-progress"
                                        className="text-xs font-extrabold uppercase tracking-[0.22em] text-[#A4803F]"
                                    >
                                        Question {step + 1} of {QUESTIONS.length}
                                    </p>
                                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1A3B2B] text-[#D9B96A]">
                                        <q.icon size={19} aria-hidden="true" />
                                    </span>
                                </div>
                                <div
                                    className="mt-4 h-1 overflow-hidden rounded-full bg-[#E8E6E1]"
                                    role="progressbar"
                                    aria-valuenow={step + 1}
                                    aria-valuemin={1}
                                    aria-valuemax={QUESTIONS.length}
                                >
                                    <motion.div
                                        data-testid="mini-check-progress-bar"
                                        className="h-full rounded-full bg-[#C2A160]"
                                        initial={false}
                                        animate={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }}
                                        transition={{ duration: 0.45, ease: EASE }}
                                    />
                                </div>

                                <p className="eyebrow-green mt-8">{q.area}</p>
                                <h2 className="font-display mt-3 text-2xl font-medium leading-snug tracking-tight text-[#0A1711] sm:text-3xl">
                                    {q.text}
                                </h2>

                                <div className="mt-9 grid gap-3" role="group" aria-label="Answer choices">
                                    {ANSWERS.map(({ key, label, sub, points, icon: Icon }) => {
                                        const selected = answers[step] === points;
                                        return (
                                            <button
                                                key={key}
                                                type="button"
                                                data-testid={`mini-check-answer-${key}`}
                                                onClick={() => choose(step, points)}
                                                className={`group flex items-center gap-4 rounded-xl border p-5 text-left transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5 ${
                                                    selected
                                                        ? "border-[#C2A160] bg-[#C2A160]/10"
                                                        : "border-[#E8E6E1] bg-[#FDFBF7] hover:border-[#1A3B2B]"
                                                }`}
                                            >
                                                <Icon
                                                    size={22}
                                                    aria-hidden="true"
                                                    className={selected ? "text-[#A4803F]" : "text-[#1A3B2B]/50 transition-colors group-hover:text-[#1A3B2B]"}
                                                />
                                                <span>
                                                    <span className="block text-sm font-bold text-[#0A1711]">{label}</span>
                                                    <span className="mt-0.5 block text-xs text-[#4A5D54]">{sub}</span>
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>

                                {step > 0 && (
                                    <button
                                        type="button"
                                        data-testid="mini-check-back"
                                        onClick={() => {
                                            const prev = step - 1;
                                            const next = [...answers];
                                            next[prev] = null;
                                            setAnswers(next);
                                            setStep(prev);
                                        }}
                                        className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-[#1A3B2B] transition-colors hover:text-[#A4803F]"
                                    >
                                        <ArrowLeft size={15} aria-hidden="true" />
                                        Back
                                    </button>
                                )}
                            </motion.div>
                        )}

                        {stage === "result" && (
                            <motion.div
                                key="result"
                                data-testid="mini-check-result"
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.55, ease: EASE }}
                                className="rounded-2xl border border-[#E8E6E1] bg-white p-8 shadow-[0_24px_70px_rgba(10,23,17,0.08)] sm:p-12"
                            >
                                <Eyebrow>Your Starting Point</Eyebrow>
                                <div className="mt-6 flex flex-wrap items-center gap-6">
                                    <div
                                        data-testid="mini-check-score"
                                        className="flex h-28 w-28 shrink-0 flex-col items-center justify-center rounded-full border-2 border-[#C2A160] bg-[#04150D]"
                                    >
                                        <span className="font-display text-4xl font-medium leading-none text-[#D9B96A]">
                                            {score}
                                        </span>
                                        <span className="mt-1 text-[10px] font-extrabold uppercase tracking-[0.2em] text-[#FDFBF7]/60">
                                            of 10
                                        </span>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-3">
                                            <band.icon size={26} className="shrink-0 text-[#A4803F]" aria-hidden="true" />
                                            <h2
                                                data-testid="mini-check-band"
                                                className="font-display text-2xl font-medium tracking-tight text-[#0A1711] sm:text-3xl"
                                            >
                                                {band.title}
                                            </h2>
                                        </div>
                                        <div className="mt-3 h-1.5 max-w-xs overflow-hidden rounded-full bg-[#E8E6E1]">
                                            <motion.div
                                                className="h-full rounded-full bg-[#C2A160]"
                                                initial={{ width: 0 }}
                                                animate={{ width: `${(score / 10) * 100}%` }}
                                                transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <p className="mt-6 text-base leading-relaxed text-[#4A5D54]">{band.text}</p>

                                <div className="mt-8 rounded-xl border border-[#E8E6E1] bg-[#FDFBF7] p-6">
                                    <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-[#A4803F]">
                                        Your answers at a glance
                                    </p>
                                    <ul className="mt-4 grid gap-2.5" data-testid="mini-check-recap">
                                        {QUESTIONS.map((question, i) => (
                                            <li
                                                key={question.area}
                                                className="flex flex-wrap items-center justify-between gap-2 text-sm"
                                            >
                                                <span className="font-semibold text-[#0A1711]">{question.area}</span>
                                                <span
                                                    className={`rounded-full px-3 py-1 text-xs font-bold ${
                                                        answers[i] === 2
                                                            ? "bg-[#1A3B2B]/10 text-[#1A3B2B]"
                                                            : answers[i] === 1
                                                              ? "bg-[#C2A160]/15 text-[#A4803F]"
                                                              : "bg-[#0A1711]/5 text-[#4A5D54]"
                                                    }`}
                                                >
                                                    {answerLabel(answers[i])}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <p
                                    data-testid="mini-check-disclaimer"
                                    className="mt-6 rounded-xl border border-[#C2A160]/30 bg-[#C2A160]/5 p-4 text-xs leading-relaxed text-[#4A5D54]"
                                >
                                    Your Mini Sustainability Check is an introductory
                                    self-assessment only. It is not a GGH certification,
                                    accreditation, professional audit, or official sustainability
                                    rating.
                                </p>

                                <div className="mt-10 border-t border-[#E8E6E1] pt-9">
                                    <h3 className="font-display text-2xl font-medium tracking-tight text-[#0A1711]">
                                        Ready to Go Further?
                                    </h3>
                                    <p className="mt-3 max-w-xl text-sm leading-relaxed text-[#4A5D54]">
                                        Join Global Green Hotel to access more comprehensive
                                        sustainability assessments, improvement resources, progress
                                        tracking, training, and other GGH hotel services as they
                                        become available.
                                    </p>
                                    <div className="mt-6 flex flex-wrap gap-4">
                                        <Link to="/join" data-testid="mini-check-join" className="btn-gold">
                                            Join GGH
                                            <ArrowUpRight size={16} aria-hidden="true" />
                                        </Link>
                                        <Link
                                            to="/for-hotels"
                                            data-testid="mini-check-services"
                                            className="btn-outline-green"
                                        >
                                            Explore Hotel Services
                                        </Link>
                                    </div>

                                    {leadStatus === "success" ? (
                                        <div
                                            data-testid="mini-check-lead-success"
                                            role="status"
                                            className="mt-8 flex items-start gap-3 rounded-xl border border-[#1A3B2B]/25 bg-[#1A3B2B]/5 p-5"
                                        >
                                            <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-[#1A3B2B]" aria-hidden="true" />
                                            <p className="text-sm leading-relaxed text-[#0A1711]">
                                                Thank you — we've received your details and will
                                                share more about GGH with you soon.
                                            </p>
                                        </div>
                                    ) : (
                                        <form
                                            onSubmit={submitLead}
                                            noValidate
                                            data-testid="mini-check-lead-form"
                                            className="mt-8 rounded-xl border border-[#E8E6E1] bg-[#FDFBF7] p-6"
                                        >
                                            <p className="text-sm font-bold text-[#0A1711]">
                                                Want to take the next step?
                                            </p>
                                            <p className="mt-1 text-xs leading-relaxed text-[#4A5D54]">
                                                Optional — leave your hotel name and business email
                                                and we'll share more about GGH.
                                            </p>
                                            <div className="mt-5 grid gap-4 sm:grid-cols-2">
                                                <div>
                                                    <label
                                                        htmlFor="mc-hotel"
                                                        className="mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#4A5D54]"
                                                    >
                                                        Hotel / Business Name
                                                    </label>
                                                    <input
                                                        id="mc-hotel"
                                                        data-testid="mini-check-lead-hotel"
                                                        type="text"
                                                        value={lead.hotel_name}
                                                        onChange={(e) => {
                                                            setLead((l) => ({ ...l, hotel_name: e.target.value }));
                                                            setLeadErrors((er) => ({ ...er, hotel_name: undefined }));
                                                        }}
                                                        aria-invalid={!!leadErrors.hotel_name}
                                                        className={`input-field ${leadErrors.hotel_name ? "input-error" : ""}`}
                                                        placeholder="Your hotel"
                                                    />
                                                    {leadErrors.hotel_name && (
                                                        <p className="mt-2 text-xs font-semibold text-red-700" role="alert">
                                                            {leadErrors.hotel_name}
                                                        </p>
                                                    )}
                                                </div>
                                                <div>
                                                    <label
                                                        htmlFor="mc-email"
                                                        className="mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#4A5D54]"
                                                    >
                                                        Business Email
                                                    </label>
                                                    <input
                                                        id="mc-email"
                                                        data-testid="mini-check-lead-email"
                                                        type="email"
                                                        value={lead.email}
                                                        onChange={(e) => {
                                                            setLead((l) => ({ ...l, email: e.target.value }));
                                                            setLeadErrors((er) => ({ ...er, email: undefined }));
                                                        }}
                                                        aria-invalid={!!leadErrors.email}
                                                        className={`input-field ${leadErrors.email ? "input-error" : ""}`}
                                                        placeholder="you@yourhotel.com"
                                                    />
                                                    {leadErrors.email && (
                                                        <p className="mt-2 text-xs font-semibold text-red-700" role="alert">
                                                            {leadErrors.email}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                type="submit"
                                                data-testid="mini-check-lead-submit"
                                                disabled={leadStatus === "submitting"}
                                                className="btn-green mt-5 disabled:cursor-not-allowed disabled:opacity-60"
                                            >
                                                {leadStatus === "submitting" ? "Sending…" : "Keep Me Informed"}
                                            </button>
                                        </form>
                                    )}

                                    <button
                                        type="button"
                                        data-testid="mini-check-retake"
                                        onClick={retake}
                                        className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-[#1A3B2B] transition-colors hover:text-[#A4803F]"
                                    >
                                        <RotateCcw size={15} aria-hidden="true" />
                                        Retake Assessment
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </section>

            <section className="grain relative overflow-hidden bg-[#04150D] py-20 sm:py-24">
                <div className="relative z-[2] mx-auto max-w-[820px] px-5 sm:px-8">
                    <Reveal>
                        <Eyebrow dark>What Happens Next</Eyebrow>
                        <h2 className="font-display mt-4 text-3xl font-medium tracking-tight text-[#FDFBF7] sm:text-4xl">
                            From a quick check to a complete picture
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#FDFBF7]/70 md:text-lg">
                            This mini check introduces the thinking behind the GGH framework.
                            Member hotels complete the full GGH Sustainability Assessment — eight
                            areas, a private dashboard, a tailored improvement plan and progress
                            tracking over time.
                        </p>
                    </Reveal>
                    <Reveal delay={0.25}>
                        <div className="mt-8 flex flex-wrap gap-4">
                            <Link to="/join" data-testid="mini-check-bottom-join" className="btn-gold">
                                Join GGH
                                <ArrowUpRight size={16} aria-hidden="true" />
                            </Link>
                            <Link to="/framework" data-testid="mini-check-bottom-framework" className="btn-outline-light">
                                Explore the Framework
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>
        </main>
    );
};

export default MiniAssessment;
